import hyperdom from 'hyperdom'
import * as constants from 'root-config/constants.js'
import format from 'root-core/utils/text/stringFormatFilter.js'
import formatPrice from '../../../packages/ui-helpers/formatPrice.js'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import responsiveRender from '../../../responsiveRender.js'
import SectionComponentBase from './sectionComponentBase.jsx'
import TravelProtectionPlanType from 'root-packages/travelProtection/index.js'

export default class TravelProtectionPlans extends SectionComponentBase {
  constructor ({
    config,
    features,
    quoteCriteria,
  }) {
    super({sectionName: 'travel-protection'})
    this.config = config
    this.features = features
    this.quoteCriteria = quoteCriteria
    this.insurance = {
      mandatoryNotes: config.insurance.mandatoryNotes,
      productInfoUrl: config.insurance.productInfoUrl,
      text: config.insurance.text,
      tooltip: config.insurance.tooltip,
      isHidden: this.config.insurance.isHidden,
    }
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  onTravelProtectionSelectionChange (passenger, planType) {
    if (passenger.travelProtection === planType) {
      passenger.ontarioResident = false
      delete passenger.travelProtection
    } else {
      passenger.travelProtection = planType
    }
  }

  render () {

    if (this.anyWithTravelProtection() && !this.insurance.isHidden) {
      const callToActionText = responsiveRender('small',
        this.config.passengers.travelInsurance_callToAction_mobile,
        this.config.passengers.travelInsurance_callToAction
      )
      return <div className='travel-protection'>
        <div className='o-accent-box'>
          <p className='discounts-offers__title'>
            <span>{this.insurance.text}</span>
            {this.renderPlanTooltip('standard')}
          </p>
          <p className='discounts-offers__subtitle'>
            <span className='travel-protection-call-to-action'>
              {hyperdom.rawHtml('span',
                format(callToActionText,
                  this.insurance.productInfoUrl,
                  this.insurance.text
                )
              )}
            </span>
          </p>
          {responsiveRender('small',
            () => this.renderMobile(),
            () => this.renderDesktop()
          )}
          {this.renderMandatoryNotes()}
          {this.renderInsuranceNotes()}
        </div>
      </div>
    }
  }

  renderDesktop () {
    return this.renderPassengersList()
  }

  renderMobile () {
    if (this.quoteCriteria.passengers.some(p => p.travelProtection)) {
      this.toggledForMobile = true
    }
    return [
      this.renderRadioButtons({
        onNoClick: () => this.quoteCriteria.passengers.forEach(p => delete p.travelProtection),
      }),
      this.toggledForMobile ? this.renderPassengersList() : undefined,
    ]
  }

  renderPassengersList () {
    return [
      this.renderPassengersListForPlan(TravelProtectionPlanType.standard),
      ...(this.anyWithProtectionPlus()
        ? [
          <p className={['discounts-offers__title', 'travel-protection-plus__title']}>
            <span>{this.insurance.text} Plus</span>
            {this.renderPlanTooltip('plus')}
          </p>,
          this.renderPassengersListForPlan(TravelProtectionPlanType.plus),
        ]
        : []),
    ]
  }

  renderPassengersListForPlan (planType = TravelProtectionPlanType.standard) {
    const {quoteCriteria} = this
    return <div className={['travel-protection__passengers', `travel-protection--${planType}`]}>
      {quoteCriteria.passengers.map((passenger, i) => {
        if (!passenger.ontarioResident) {
          passenger.ontarioResident = false
        }
        const _travelProtectionPlan = planType === TravelProtectionPlanType.standard
          ? passenger.travelProtectionPlan
          : passenger.travelProtectionPlanPlus
        if (_travelProtectionPlan) {
          const price = passenger.ontarioResident
            ? _travelProtectionPlan.price * (1 + constants.ontarioTravelProtectionTax)
            : _travelProtectionPlan.price
          return <label className={['travel-protection__passenger', 'o-choice-item', '-fullWidth', 'u-margin-bottom-none']}>
            <input type='radio'
              name={`travelProtection${i}`}
              className='o-choice-item__checkbox'
              binding={{
                get: () => passenger.travelProtection,
                set: () => {
                  this.onTravelProtectionSelectionChange(passenger, planType)
                },
              }}
              value={planType}
            />
            <div className='o-choice-item__content'>
              <span className={i === 0 ? 'passenger-name-semibold' : 'passenger-name'}>{passenger.fullName()}</span>
              <span> - </span>
              <strong className='price'>
                {formatPrice({
                  currency: quoteCriteria.departure.prices.currency,
                  amount: price,
                })}
              </strong>
              {this.renderOntarioResidentChoice(passenger, i)}
            </div>
          </label>
        }
      })}
    </div>
  }

  renderOntarioResidentChoice (passenger, index) {
    return this.config.country === 'Canada' && passenger.travelProtection
      ? <div className='insurance__canada'>
        <div className='insurance__canada-inner'>
          <div className='insurance__canada-row insurance__not-ontario'>
            <input type='radio'
              id={`residency_pax${index}-not-ontario`}
              binding={[passenger, 'ontarioResident']}
              value={false}
              name={`residency_pax${index}`}
            />
            <label for={`residency_pax${index}-not-ontario`}>does not reside in Ontario</label>
          </div>
          <div className='insurance__canada-row insurance__ontario'>
            <input type='radio'
              id={`residency_pax${index}-ontario`}
              binding={[passenger, 'ontarioResident']}
              value
              name={`residency_pax${index}`}
            />
            <label for={`residency_pax${index}-ontario`}>resides in Ontario</label>
          </div>
        </div>
      </div>
      : undefined
  }

  renderInsuranceNotes () {
    const textForCanada = `Please note that residents of Ontario are subject to an additional Charge of PST on their Travel Protection plan.`
    return this.config.country === 'Canada'
      ? <p className='field-note insurance__notes'>{textForCanada}</p>
      : undefined
  }

  renderPlanTooltip (planType) {
    const tooltipItems = this.insurance.tooltip && this.insurance.tooltip[planType]
    return tooltipItems
      ? <span className='travel-protection-tooltip tooltip'>
        <div className='tooltip__content'>
          {tooltipItems.map(item => <p className='travel-protection-tooltip__item'>{item}</p>)}
        </div>
      </span>
      : undefined
  }

  renderMandatoryNotes () {
    return this.insurance.mandatoryNotes && this.insurance.mandatoryNotes.length
      ? <div className='travel-protection__mandatory-notes'>
        <span>IMPORTANT:</span>
        <ul className='travel-protection__mandatory-notes-list'>
          {this.insurance.mandatoryNotes.map((note) => <li>{note}</li>)}
        </ul>
      </div>
      : undefined
  }

  anyWithTravelProtection () {
    return this.anyWithStandardProtection()
      || this.anyWithProtectionPlus()
  }

  anyWithStandardProtection () {
    return this.quoteCriteria.passengers.some(({travelProtectionPlan}) => {
      return (travelProtectionPlan && !travelProtectionPlan.isMandatory)
    })
  }

  anyWithProtectionPlus () {
    return this.quoteCriteria.passengers.some(({travelProtectionPlanPlus}) => {
      return (travelProtectionPlanPlus && !travelProtectionPlanPlus.isMandatory)
    })
  }
}
